import * as React from 'react';
import { useState, useEffect, FunctionComponent } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import Button from '@murphy-frontend/web-core/components/Button';
import Spinner from '@murphy-frontend/web-core/components/Spinner';
import SearchBar from '@murphy-frontend/web-core/components/SearchBar';
import { FilterType } from '@murphy-frontend/web-core/components/FilterControl';
import DashboardsList, { DashboardsListViewModel } from './components/DashboardsList';
import { openInNewTab } from '@murphy-frontend/common/utils';
import { getConvertedDate } from '@murphy-frontend/common/services/TimeService';
import { useDashboards } from '../../common/api/Dashboards/queries';
import { useEditDashboard } from '../../common/api/Dashboards/mutations';
import { Dashboard, UpdateDashboardRequest } from '../../common/api/Dashboards/DashboardsApi';
import { useCustomer } from '../../common/contexts/CustomerContext';
import { OnboardingFlowIds, useOnboarding } from '../../common/contexts/OnboardingContext';
import { parseHtml } from '@murphy-frontend/web-core/features/RichText/helpers';
import { ModalConfiguration } from '@murphy-frontend/web-core/components/Modal';
import RichTextDisplay from '@murphy-frontend/web-core/features/RichText/RichTextDisplay';
import { useModal } from '@murphy-frontend/web-core/contexts/ModalContext';
import { IconContext } from 'react-icons';
import { AiOutlinePlus } from 'react-icons/ai';
import Filters from './components/Filters';
import FilterToggle from '@murphy-frontend/web-core/components/FilterToggle';

interface DashboardsProps {
  spawnModal: any;
  translations: Record<string, string>;
  currentScreenSize: any;
  currentwidth: any;
  timeZone: any;
  language: any;
}

const CrisisDashboards: FunctionComponent<DashboardsProps> = ({
  spawnModal,
  translations,
  timeZone,
  language,
  currentScreenSize,currentwidth,
}) => {
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  const { customer } = useCustomer();
  const [filteredDashboards, setFilteredDashboards] = useState([]);
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [allFilters, setAllFilters] = useState([]);
  const [filterCheck, setFilterCheck] = useState([]);
  const [currentSearchText, setCurrentSearchText] = useState('');

  const [stepsEnabled, setStepsEnabled] = useState<boolean>(false);
  const [onboardingTerminated, setOnboardingTerminated] = useState<boolean>(null);
  const { setOnboardingIdIsComplete, incompletedOnboardingFlows, isLoading: onBoardingIsLoading, resetOnboarding, setCurrentStep, onboardingsCurrentStep } = useOnboarding();

  const { isLoading, isError, data, error } = useDashboards(customer?.Id);
  const { mutate: editDashboard, isPending: editDashboardIsLoading } = useEditDashboard(customer?.Id, translations);

  const mapDashboards = (rawDataDashboards: Dashboard[]): DashboardsListViewModel[] =>
    rawDataDashboards.map((dash) => ({
      id: dash.dashboardid,
      dashboardname: decodeURIComponent(dash.Name),
      isarchived: dash.Arkiverad,
      link: dash.adminlink,
      dashboardtypeid: dash.DashboardTypeId,
      createddatetime: getConvertedDate(dayjs.utc(dash.CreatedDateTime), timeZone),
      lastupdatedatetime: dash.LastUpdateDateTime ? getConvertedDate(dayjs.utc(dash.LastUpdateDateTime), timeZone) : '',
    }));

  const onSearchChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setCurrentSearchText(newValue);
  };

  function ErrorMessage() {
    return <p>{error.message}</p>;
  }

  const onClickEditDashboard = (dashboardRow: DashboardsListViewModel) => {
    const url = `/dashboards/${dashboardRow.id}`;
    navigate(url);
  };

  const onSubmitArchiveDashboard = (dashboardId: string) => {
    const editDashboardRequest: UpdateDashboardRequest = {
      DashboardId: dashboardId,
      ArchiveBoard: true,
    };
    editDashboard(editDashboardRequest);
  };

  const onSubmitActivateDashboard = (dashboardId: string) => {
    const editDashboardRequest: UpdateDashboardRequest = {
      DashboardId: dashboardId,
      ActivateBoard: true,
    };
    editDashboard(editDashboardRequest);
  };

  function ArchiveConfirmationBody(dashboardname: string) {
    return (
      <p>
        {translations.archive}{' '}
        <strong>{dashboardname}</strong>{' '}
        ?
      </p>
    );
  }

  function ActivateConfirmationBody(dashboardname: string) {
    return (
      <p>
        {translations.activate}{' '}
        <strong>{dashboardname}</strong>{' '}
        ?
      </p>
    );
  }

  const onClickArchiveDashboard = (dashboardRow: DashboardsListViewModel) => {
    const modalConf: ModalConfiguration = {
      title: `${translations.archive} dashboard`,
      body: ArchiveConfirmationBody(dashboardRow.dashboardname),
      hideButton: false,
      buttonText: translations.archive,
      type: 'warning',
      okCallback: () => onSubmitArchiveDashboard(dashboardRow.id),
    };
    spawnModal(modalConf);
  };

  const onClickActivateDashboard = (dashboardRow: DashboardsListViewModel) => {
    const modalConf: ModalConfiguration = {
      title: `${translations.activate} dashboard`,
      body: ActivateConfirmationBody(dashboardRow.dashboardname),
      hideButton: false,
      buttonText: translations.activate,
      type: 'info',
      okCallback: () => onSubmitActivateDashboard(dashboardRow.id),
    };
    spawnModal(modalConf);
  };

  const onClickGoToDashboard = (row: DashboardsListViewModel) => {
    const linkToDashboard = `/dashboard/${row.id}`;
    openInNewTab(linkToDashboard);
  };

  const onToggleFilter = (filt: FilterType[], fromDate: Date | undefined | null, toDate: Date | undefined | null) => {
    setAllFilters(filt);
    setStartDate(fromDate);
    setEndDate(toDate);
  };

  const onClickNewDashboard = () => {
    navigate('/dashboards/new');
  };

  useEffect(() => {
    if (data && data.length > 0) {
      let newFilteredDashboards = mapDashboards(data);
      let newData = [];

      if (currentSearchText) {
        const searchValue = currentSearchText.toUpperCase();
        newFilteredDashboards = newFilteredDashboards.filter((p) =>
          p.dashboardname.toUpperCase().startsWith(searchValue)
        );
      }
      if (allFilters) {
        for (let i = 0; i < allFilters.length; i++) {
          const currentFilter = allFilters[i];
          if (currentFilter.isActive === true || startDate || endDate) {
            let data = newFilteredDashboards
              .filter((dash) => {
                if (dash[currentFilter.columnname] === currentFilter.filtervalue) {
                  return dash;
                }
              })
              .filter((dash) => {
                if (!startDate) return dash;
                return startDate && startDate.getTime() < new Date(dash.createddatetime).getTime();
              })
              .filter((dash) => {
                if (!endDate) return dash;
                return endDate && endDate.getTime() > new Date(dash.createddatetime).getTime();
              });
            newData = newData.concat(data);
          }
        }
      }
      if (!startDate && !endDate && allFilters.filter(item => item.isActive).length === 0) {
        newData = newFilteredDashboards;
      }
      setFilteredDashboards(newData);

      const checkFilters = [
        ...allFilters,
        {
          columnname: 'startDate',
          displayName: `From ${getConvertedDate(dayjs.utc(startDate), timeZone, 'MMM DD, YYYY')}`,
          isActive: startDate,
        },
        {
          columnname: 'endDate',
          displayName: `To ${getConvertedDate(dayjs.utc(endDate), timeZone, 'MMM DD, YYYY')}`,
          isActive: endDate,
        },
      ];
      setFilterCheck(checkFilters);
    }
  }, [allFilters, currentSearchText, data, timeZone, startDate, endDate]);

  const handleCheckFilter = (filter: FilterType) => {
    const updatedFilter = { ...filter, isActive: false };
    let updatedFilters = [...filterCheck];
    const idx = updatedFilters.findIndex(row => row.displayName === filter.displayName);
    updatedFilters[idx] = updatedFilter;
    setFilterCheck(updatedFilters);
    if (filter.columnname === 'startDate') {
      setStartDate(null);
    } else if (filter.columnname === 'endDate') {
      setEndDate(null);
    } else if (filter.displayName === translations.onlyactive || filter.displayName === translations.closed) {
      setAllFilters(updatedFilters.filter(item => item.displayName === translations.onlyactive || item.displayName === translations.closed));
    }
  };

  const generateInitialFilters = () => {
    const allPossibleFilters = [
      {
        displayName: translations.onlyactive,
        columnname: 'isarchived',
        filtervalue: false,
        isActive: true,
      },
      {
        displayName: translations.closed,
        columnname: 'isarchived',
        filtervalue: true,
        isActive: false,
      },
    ];
    setAllFilters(allPossibleFilters);
  };

  useEffect(() => {
    if (customer.Id) {
      generateInitialFilters();
    }
  }, [customer]);

  useEffect(() => {
    generateInitialFilters();
  }, [translations]);

  useEffect(() => {
    if (error) {
      const modalConf: ModalConfiguration = {
        title: 'Error',
        body: <ErrorMessage />,
        buttonText: 'Ok',
        type: 'error',
      };
      spawnModal(modalConf);
    }
  }, [isError]);

  useEffect(() => {
    if (onboardingTerminated === true) return;
    const shouldShowOnboarding = incompletedOnboardingFlows.map(p => p.OnboardingFlowId).includes(OnboardingFlowIds.Dashboard);
    if (shouldShowOnboarding && !onBoardingIsLoading) {
      const currentStep = onboardingsCurrentStep.get(OnboardingFlowIds.Dashboard);
      if (currentStep === 0) {
        const stepsFromApi = incompletedOnboardingFlows.find(p => p.OnboardingFlowId === OnboardingFlowIds.Dashboard)?.StepsHtmlContents;
        const introText = stepsFromApi.get(1);
        const parsedHtmlIntro = introText ? parseHtml(introText) : "";
        const onClickOk = () => {
          setCurrentStep(OnboardingFlowIds.Dashboard, 1);
          closeModal();
          setStepsEnabled(true);
        };

        const onClickClose = () => {
          setOnboardingIsComplete();
        };

        const modalConf: ModalConfiguration = {
          okCallback: onClickOk,
          onCloseModal: onClickClose,
          type: 'info',
          centerContent: true,
          body: <div className="intro-outro-container">
            <RichTextDisplay content={parsedHtmlIntro} />
          </div>,
          title: "Tutorial",
          buttonText: "Start",
          position: 'center',
        };
        openModal(modalConf);
      } else {
        setStepsEnabled(true);
      }
    }
  }, [incompletedOnboardingFlows, onBoardingIsLoading, onboardingsCurrentStep]);

  const setOnboardingIsComplete = () => {
    setOnboardingTerminated(true);
    setOnboardingIdIsComplete(OnboardingFlowIds.Dashboard);
    setCurrentStep(OnboardingFlowIds.Dashboard, 0);
    setStepsEnabled(false);
  };

  if (editDashboardIsLoading || isLoading || !translations) {
    return <Spinner isGlobal />;
  }

  const resetFilters = () => {
    const filters = [...allFilters].map(filter => ({ ...filter, isActive: false }));
    setStartDate(null);
    setEndDate(null);
    const filterscheck = [...filterCheck].map(filter => ({ ...filter, isActive: false }));
    setAllFilters(filters);
    setFilterCheck(filterscheck);
  };

  const onClickFilter = () => {
    const modalConf: ModalConfiguration = {
      body: (
        <Filters
          allFilters={allFilters}
          startDate={startDate}
          endDate={endDate}
          closeModal={closeModal}
          toggleFilter={(filter, fromDate, toDate) => onToggleFilter(filter, fromDate, toDate)}
          reset={resetFilters}
          translations={translations}
        />
      ),
      buttonText: "Ok",
      position: 'center',
      hideButton: true,
    };
    openModal(modalConf);
  };


  // Determine sidebar width based on screen size and collapsed state
  const isMobile = currentwidth <= 768;
  const sidebarWidth = isMobile ? 60 : 250;
  const marginLeft = isMobile ? 0 : sidebarWidth;

  return (
    <div style={{ }}>
      <section className='header-btns'>
				<div className='header-back-btn'>
					<h2>{translations ? translations.apphsbtncrisis : "Crisis Management"}</h2>
				</div>
				<div className='header-right-btn'>
           <div style={{ marginLeft: isMobile ? '0' : '20px' }}>
					<Button  buttonSize={"btn--medium"} onClick={onClickNewDashboard}>
              {translations.createOperation || "Create a new operation"}
					</Button></div>
				</div>
			</section>
  
      <div
        className="three"
        style={{
          display: 'flex',
          padding: isMobile ? '10px' : '20px',
          width: '100%',
          maxWidth: isMobile ? '100%' : '1200px',
          marginTop: '20px',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '10px' : '20px',
          alignItems: isMobile ? 'stretch' : 'center',
        }}
      >
        <div style={{ width: isMobile ? '100%' : '80%', maxWidth: '600px' }}>
          <SearchBar
            val={currentSearchText}
            handleChange={onSearchChanged}
            placeholder={translations["placeholder-lang-search-action"]}
            showSearchIcon={true}
          />
        </div>
        <div style={{ width: isMobile ? '100%' : 'auto' }}>
          <Button isDarkButton={true} onClick={onClickFilter}>
            {translations["lang-filter"]}
          </Button>
        </div>
      </div>
      <div
        style={{
          width: '100%',
          maxWidth: isMobile ? '100%' : '1300px',
          display: 'flex',
          flexDirection: 'column',
          padding: isMobile ? '10px' : '20px',
          marginTop: '10px',
        }}
      >
        <div className="filters-row" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '15px' }}>
          {filterCheck.map(
            (filter) =>
              filter.isActive && (
                <FilterToggle
                  filterName={filter.displayName}
                  key={filter.displayName}
                  checked={filter.isActive}
                  handleClick={() => handleCheckFilter(filter)}
                />
              )
          )}
          {filterCheck.filter((filt) => filt.isActive).length > 0 && (
            <p
              style={{
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: isMobile ? '14px' : '16px',
              }}
              onClick={resetFilters}
            >
              {translations.operationsClearAll || "Clear all"}
            </p>
          )}
        </div>
        <div style={{ width: '100%', maxWidth: isMobile ? '100%' : '1300px' }}>
          <DashboardsList
            dashboards={filteredDashboards}
            onClickGoToDashboard={onClickGoToDashboard}
            onClickEditDashboard={onClickEditDashboard}
            onClickArchiveDashboard={onClickArchiveDashboard}
            onClickActivateDashboard={onClickActivateDashboard}
            translations={translations}
            language={language}
            style={{ width: '100%' }}
          />
        </div>
      </div>
    </div>
  );
};

export default CrisisDashboards;